/** Internal notes on a conversation: readable by every desk user, added by admins and operators. */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { fmtDateTime } from '../../lib/format'
import type { DeskChannel, DeskNote } from '../../lib/supportDesk'

type Props = { channel: DeskChannel; id: number; canAct: boolean }

export function NotesPanel({ channel, id, canAct }: Props) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const notes = useQuery<DeskNote[]>({
    queryKey: ['support', 'notes', channel, id],
    queryFn: () => api.get<DeskNote[]>(`/api/support/conversations/${channel}/${id}/notes`),
    retry: false,
  })
  const add = useMutation({
    mutationFn: (body: string) => api.post<DeskNote>(`/api/support/conversations/${channel}/${id}/notes`, { body }),
    onSuccess: () => { setText(''); setOpen(false); qc.invalidateQueries({ queryKey: ['support'] }) },
  })
  const list = notes.data ?? []

  return (
    <section aria-label="Internal notes" className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="label">Internal notes{list.length ? ` · ${list.length}` : ''}</span>
        {canAct && !open && (
          <button type="button" className="label-amber hover:underline ml-auto" onClick={() => setOpen(true)}>Add note</button>
        )}
      </div>
      {notes.isError && <p className="text-red-400 text-sm">Couldn't load notes. Try again.</p>}
      {notes.data && list.length === 0 && !open && <p className="text-paper-trace text-sm">No notes yet.</p>}
      <ol className="space-y-3">
        {list.map(n => (
          <li key={n.id} className="border-l-2 border-dashed border-ink-500 pl-3">
            <div className="label">{n.author_email ?? 'Former staff'} · {fmtDateTime(n.created_at)}</div>
            <p className="text-paper-dim text-sm whitespace-pre-wrap">{n.body}</p>
          </li>
        ))}
      </ol>
      {canAct && open && (
        <form className="space-y-2" onSubmit={e => { e.preventDefault(); if (text.trim()) add.mutate(text) }}>
          <label className="block">
            <span className="label">Note for staff only, never sent to the customer</span>
            <textarea rows={3} maxLength={4000} value={text} onChange={e => setText(e.target.value)} autoFocus
              className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2" />
          </label>
          <div className="flex gap-3 items-center">
            <button type="submit" className="label-amber hover:underline disabled:opacity-50" disabled={!text.trim() || add.isPending}>
              {add.isPending ? 'Saving…' : 'Save note'}
            </button>
            <button type="button" className="label hover:underline" onClick={() => { setOpen(false); setText(''); add.reset() }}>Cancel</button>
            {add.isError && <span className="text-red-400 text-sm">Couldn't save the note. Try again.</span>}
          </div>
        </form>
      )}
    </section>
  )
}
